import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PawPrint, Fingerprint, Delete, Loader2 } from "lucide-react";
import { LOGO_URL } from "@/i18n/translations";

// Shared animated primitives for the staff app
export const spring = { type: "spring", stiffness: 320, damping: 28 };

export const ScreenIn = ({ children, k }) => (
  <motion.div
    key={k}
    initial={{ opacity: 0, x: 26, scale: 0.985 }}
    animate={{ opacity: 1, x: 0, scale: 1 }}
    exit={{ opacity: 0, x: -26, scale: 0.985 }}
    transition={spring}
    className="pb-28"
  >
    {children}
  </motion.div>
);

export const stagger = { show: { transition: { staggerChildren: 0.06 } } };
export const rise = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: spring } };

export const Skeleton = ({ className }) => (
  <div className={`relative overflow-hidden rounded-2xl bg-brand/10 ${className}`}>
    <motion.div
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
      animate={{ x: ["-100%", "100%"] }}
      transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
    />
  </div>
);

// ---------- Splash ----------
export const Splash = () => (
  <motion.div
    className="fixed inset-0 z-[60] grid place-items-center bg-brand-cream"
    exit={{ opacity: 0, scale: 1.06 }}
    transition={{ duration: 0.5, ease: "easeInOut" }}
    data-testid="app-splash"
  >
    <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 bg-brand-soft/60 rounded-full blur-3xl" />
    <div className="pointer-events-none absolute -bottom-32 -left-32 h-64 w-64 bg-brand/5 rounded-full blur-3xl" />
    <div className="relative z-10 flex flex-col items-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0, rotate: -6 }}
        animate={{ scale: 1, opacity: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 180, damping: 16 }}
        className="grid place-items-center h-28 w-28 rounded-[2rem] bg-white shadow-[0_20px_60px_rgba(134,77,99,0.25)]"
      >
        <motion.img
          src={LOGO_URL}
          alt="Care For Me"
          className="w-20 object-contain"
          animate={{ scale: [1, 1.06, 1] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35, duration: 0.5 }}
        className="mt-5 flex items-center gap-2 text-brand-ink/50"
      >
        <PawPrint className="h-4 w-4 text-brand" />
        <span className="text-sm font-bold tracking-[0.25em] uppercase">Care For Me</span>
      </motion.div>
    </div>
  </motion.div>
);

// ---------- Personalized animated greeting ----------
export const Greeting = ({ name, text }) => (
  <motion.div
    className="fixed inset-0 z-[60] grid place-items-center bg-brand-cream"
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4 }}
    data-testid="app-greeting"
  >
    <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 bg-brand-soft/60 rounded-full blur-3xl" />
    <div className="relative z-10 text-center px-6">
      <motion.div
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 15 }}
        className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-brand text-brand-cream shadow-[0_16px_44px_rgba(134,77,99,0.35)]"
      >
        <PawPrint className="h-9 w-9" />
      </motion.div>
      <motion.p
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.45 }}
        className="mt-6 text-brand-ink/50 font-bold tracking-widest uppercase text-sm"
      >
        {text},
      </motion.p>
      <div className="mt-1 font-heading text-5xl font-medium text-brand-ink overflow-hidden">
        <motion.span
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 120, damping: 18 }}
          className="inline-block"
        >
          {name}
        </motion.span>
      </div>
    </div>
  </motion.div>
);

// ---------- Numeric keypad ----------
const PadButton = ({ children, onPress, ghost }) => (
  <motion.button
    type="button"
    whileTap={{ scale: 0.86 }}
    transition={spring}
    onClick={onPress}
    className={`h-16 rounded-2xl text-2xl font-heading font-semibold grid place-items-center transition-colors ${
      ghost ? "text-brand-ink/50" : "bg-white border border-brand/10 text-brand-ink shadow-[0_4px_14px_rgba(134,77,99,0.08)] active:bg-brand-soft/60"
    }`}
  >
    {children}
  </motion.button>
);

export const PinPad = ({ value, onChange, maxLen = 6, onBio, shake, dynamicLen = false }) => {
  const press = (d) => { if (value.length < maxLen) onChange(value + d); };
  const dots = dynamicLen ? Math.max(4, value.length) : maxLen;
  return (
    <div className="w-full max-w-xs mx-auto" data-testid="pinpad">
      <motion.div
        animate={shake ? { x: [0, -10, 10, -8, 8, 0] } : {}}
        transition={{ duration: 0.4 }}
        className="flex justify-center gap-3 mb-7"
      >
        {Array.from({ length: dots }).map((_, i) => (
          <motion.span
            key={i}
            animate={{ scale: i < value.length ? 1.25 : 1 }}
            className={`h-3.5 w-3.5 rounded-full transition-colors ${i < value.length ? "bg-brand" : "bg-brand/20"}`}
          />
        ))}
      </motion.div>
      <div className="grid grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <PadButton key={d} onPress={() => press(d)}>{d}</PadButton>
        ))}
        <PadButton ghost onPress={() => onBio?.()}>
          {onBio ? <Fingerprint className="h-6 w-6 text-brand" /> : ""}
        </PadButton>
        <PadButton onPress={() => press("0")}>0</PadButton>
        <PadButton ghost onPress={() => onChange(value.slice(0, -1))}>
          <Delete className="h-6 w-6" />
        </PadButton>
      </div>
    </div>
  );
};

// ---------- Full-screen auth backdrop ----------
export const AuthBackdrop = ({ children }) => (
  <div className="min-h-screen bg-brand-cream relative overflow-hidden flex flex-col">
    <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 bg-brand-soft/60 rounded-full blur-3xl" />
    <div className="pointer-events-none absolute -bottom-32 -left-32 h-64 w-64 bg-brand/5 rounded-full blur-3xl" />
    <div className="relative z-10 flex-1 flex flex-col">{children}</div>
  </div>
);

export const BusyOverlay = ({ show, label }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] grid place-items-center bg-brand-cream/80 backdrop-blur-sm"
      >
        <div className="flex items-center gap-3 text-brand-ink font-bold">
          <Loader2 className="h-6 w-6 animate-spin text-brand" /> {label}
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
